import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router';
import { useAktivIroda } from '../iroda/AktivIroda.tsx';
import { arelvarasCimke, elvesztOkCimke, forrasCimke, idosavCimke, kampanyCimke, tipusCimke, tolcserCimke } from '../elemzes/cimkek.ts';
import { Blokk, Cim, SorLista, Tabla, UresSor } from '../elemzes/Blokk.tsx';
import EladasokBlokk from '../elemzes/EladasokBlokk.tsx';
import KorzetKivulBlokk from '../elemzes/KorzetKivulBlokk.tsx';
import IdoszakValaszto from '../elemzes/IdoszakValaszto.tsx';
import { idoszakMondat, idoszakSzamitas, type IdoszakTipus } from '../elemzes/idoszak.ts';
import Mutatosor from '../elemzes/Mutatosor.tsx';
import { ElemzesAlakHiba, ellenorizEladasok, ellenorizElemzes, ellenorizKorzet, type EladasokValasz, type ElemzesValasz, type KorzetValasz } from '../elemzes/tipusok.ts';
import { aranySzoveg, percSzoveg } from '../elemzes/valtozas.ts';
import { honapCimke } from '../leadek/ujrahivas.ts';
import { supabase } from '../lib/supabase.ts';
import { Betolto, HibaUzenet, Mezo, OldalFej, Ures, Valaszto } from '../ui/elemek.tsx';
import Oszlopdiagram from '../ui/Oszlopdiagram.tsx';
import Tolcser from '../ui/Tolcser.tsx';

const MINDEN = 'minden';

async function elemzesLekeres(irodaId: string | null, tol: string, ig: string): Promise<ElemzesValasz> {
  // A generált típusban a p_office_id string, de az adatbázis-függvény a null-t „minden látható iroda”-ként kezeli.
  const { data, error } = await supabase.rpc('elemzes', { p_office_id: irodaId as unknown as string, p_tol: tol, p_ig: ig });
  if (error) throw error;
  return ellenorizElemzes(data);
}

async function eladasokLekeres(irodaId: string | null, tol: string, ig: string): Promise<EladasokValasz> {
  const { data, error } = await supabase.rpc('elemzes_eladasok', { p_office_id: irodaId as unknown as string, p_tol: tol, p_ig: ig });
  if (error) throw error;
  return ellenorizEladasok(data);
}

async function korzetLekeres(irodaId: string | null, tol: string, ig: string): Promise<KorzetValasz> {
  const { data, error } = await supabase.rpc('elemzes_korzet', { p_office_id: irodaId as unknown as string, p_tol: tol, p_ig: ig });
  if (error) throw error;
  return ellenorizKorzet(data);
}

function szazalekOsszeg(db: number, osszes: number): string {
  return aranySzoveg(osszes > 0 ? db / osszes : null);
}

function Hiba({ hiba }: { hiba: unknown }) {
  // Az alakhiba üzenete már érthető magyar szöveg; az egyéb hibákat a közös hibaszöveg fordítja.
  if (hiba instanceof ElemzesAlakHiba) return <p role="alert" className="rounded-xl border border-hiba bg-hiba-hatter px-3.5 py-2.5 text-sm text-hiba">{hiba.message}</p>;
  return <HibaUzenet hiba={hiba} />;
}

export default function Elemzes() {
  const { irodak, aktivId, beallit, jogok } = useAktivIroda();
  const [parameterek, setParameterek] = useSearchParams();
  const mindenIroda = jogok.super && parameterek.get('iroda') === MINDEN;
  const [tipus, setTipus] = useState<IdoszakTipus>('30nap');
  const [egyedi, setEgyedi] = useState(() => {
    const i = idoszakSzamitas('30nap', new Date());
    return { tol: i.tolNap, ig: i.utolsoNap };
  });

  // Ha a sínben másik irodát választanak, a „Minden iroda” nézet véget ér.
  const elozoAktiv = useRef(aktivId);
  useEffect(() => {
    if (elozoAktiv.current !== aktivId && parameterek.has('iroda')) setParameterek({}, { replace: true });
    elozoAktiv.current = aktivId;
  }, [aktivId, parameterek, setParameterek]);

  const idoszak = useMemo(() => {
    try { return { ertek: idoszakSzamitas(tipus, new Date(), egyedi), hiba: null }; }
    catch (e) { return { ertek: null, hiba: e instanceof Error ? e.message : 'Érvénytelen időszak.' }; }
  }, [tipus, egyedi]);

  const irodaId = mindenIroda ? null : aktivId;
  const lekeres = useQuery({
    queryKey: ['elemzes', irodaId ?? MINDEN, idoszak.ertek?.tol, idoszak.ertek?.ig],
    enabled: idoszak.ertek !== null && (mindenIroda || aktivId !== null),
    queryFn: () => elemzesLekeres(irodaId, idoszak.ertek!.tol, idoszak.ertek!.ig),
  });

  // A megbízások és eladások a rögzítés napja szerint számolnak, ezért külön lekérdezés (akkor is lehet adat, ha az
  // időszakban nem érkezett lead).
  const eladasLekeres = useQuery({
    queryKey: ['elemzes-eladasok', irodaId ?? MINDEN, idoszak.ertek?.tol, idoszak.ertek?.ig],
    enabled: idoszak.ertek !== null && (mindenIroda || aktivId !== null),
    queryFn: () => eladasokLekeres(irodaId, idoszak.ertek!.tol, idoszak.ertek!.ig),
  });

  // A körzeten kívüli kérésekből nem lesz lead, ezért ez is külön lekérdezés (lead nélküli időszakban is lehet adat).
  // Csak vezetőnek és super adminnak: az értékesítő a leadeket csak a sajátjára látja, nála az arány nevezője hamis lenne.
  const korzetValasz = useQuery({
    queryKey: ['elemzes-korzet', irodaId ?? MINDEN, idoszak.ertek?.tol, idoszak.ertek?.ig],
    enabled: jogok.vezeto && idoszak.ertek !== null && (mindenIroda || aktivId !== null),
    queryFn: () => korzetLekeres(irodaId, idoszak.ertek!.tol, idoszak.ertek!.ig),
  });

  if (!aktivId && !mindenIroda) return <Ures cim="Nincs kiválasztott iroda" />;
  const d = lekeres.data;
  const ea = eladasLekeres.data;
  const kz = korzetValasz.data;
  const korzet = kz ? <KorzetKivulBlokk d={kz} /> : <Hiba hiba={korzetValasz.error} />;
  const eladasok = ea ? <EladasokBlokk d={ea} tobbIroda={mindenIroda} /> : <Hiba hiba={eladasLekeres.error} />;
  // Lead nélküli időszakban az üres blokkot nem mutatjuk; a hibát igen.
  const vanEladasAdat = !ea || ea.db > 0 || ea.megbizasok.kizarolagos + ea.megbizasok.nem_kizarolagos > 0;
  const iroda = irodak.find((i) => i.id === aktivId);
  const kinek = mindenIroda ? 'minden iroda adatai' : iroda ? `${iroda.nev} adatai` : 'az iroda adatai';
  const leiras = idoszak.ertek ? `${idoszakMondat(idoszak.ertek.tolNap, idoszak.ertek.utolsoNap)}, ${kinek}.` : undefined;

  return (
    <>
      <OldalFej cim="Elemzés" leiras={leiras}>
        <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end sm:justify-end">
          {jogok.super && (
            <Valaszto
              label="Elemzett iroda"
              className="w-full sm:w-64"
              value={mindenIroda ? MINDEN : (aktivId ?? '')}
              onChange={(e) => {
                if (e.target.value === MINDEN) { setParameterek({ iroda: MINDEN }, { replace: true }); return; }
                setParameterek({}, { replace: true });
                beallit(e.target.value);
              }}
            >
              <option value={MINDEN}>Minden iroda</option>
              {irodak.map((i) => <option key={i.id} value={i.id}>{i.nev}</option>)}
            </Valaszto>
          )}
          <IdoszakValaszto ertek={tipus} onValt={setTipus} />
        </div>
      </OldalFej>

      {tipus === 'egyedi' && (
        <div className="mb-5 grid max-w-md grid-cols-2 gap-3">
          <Mezo label="Kezdő nap" type="date" value={egyedi.tol} onChange={(e) => setEgyedi((x) => ({ ...x, tol: e.target.value }))} />
          <Mezo label="Záró nap" type="date" value={egyedi.ig} onChange={(e) => setEgyedi((x) => ({ ...x, ig: e.target.value }))} />
        </div>
      )}
      {idoszak.hiba && <p role="alert" className="mb-5 rounded-xl border border-hiba bg-hiba-hatter px-3.5 py-2.5 text-sm text-hiba">{idoszak.hiba}</p>}
      <Hiba hiba={lekeres.error} />
      {lekeres.isPending && idoszak.ertek && <Betolto />}

      {d && d.mutatok.inditasok === 0 && <Ures cim="Ebben az időszakban még nincs lead." szoveg="Válassz hosszabb időszakot, vagy nézz vissza később." />}
      {d && d.mutatok.inditasok === 0 && vanEladasAdat && <div className="mt-[18px]">{eladasok}</div>}
      {d && d.mutatok.inditasok > 0 && <Tartalom d={d} eladasok={eladasok} />}
      {d && jogok.vezeto && (kz || korzetValasz.error) && <div className="mt-[18px]">{korzet}</div>}
    </>
  );
}

function Tartalom({ d, eladasok }: { d: ElemzesValasz; eladasok: ReactNode }) {
  const m = d.mutatok;
  const legnagyobbForras = Math.max(1, ...d.forrasok.map((f) => f.db));
  const ingatlanLista = (nev: string, sorok: { cimke: string; db: number }[]) => (
    <div className="min-w-0">
      <h3 className="text-[13px] font-medium text-szoveg-halk">{nev}</h3>
      {sorok.length > 0 ? <SorLista nev={nev} szin="smaragd" sorok={sorok} /> : <UresSor szoveg="Nincs adat." />}
    </div>
  );
  return (
    <div className="grid gap-[18px]">
      <section aria-labelledby="tolcser-cim" className="rounded-[22px] border border-vonal bg-hatter px-4 py-5 sm:px-7 sm:pb-[22px] sm:pt-[26px]">
        <h2 id="tolcser-cim" className="text-[17px] font-medium tracking-[-0.015em]">
          {m.megerositett > 0 ? `Mi lett a ${m.megerositett} megerősített leadből` : 'Még nincs megerősített lead'}
        </h2>
        <p className="mt-1 text-[13px] text-szoveg-halk">Hány érdeklődő jutott el az egyes lépésekig, és mekkora arányban lépett tovább.</p>
        <Tolcser lepesek={d.tolcser.map((l) => ({ ...l, cimke: tolcserCimke(l.lepes) }))} />
        <Mutatosor most={m} elozo={d.elozo_mutatok} />
      </section>

      {eladasok}

      <div className="grid gap-[18px] lg:grid-cols-[1.35fr_1fr]">
        <Blokk cim="Beérkezett leadek naponta" leiras="A meg nem erősített indítások külön látszanak: ők nem kaptak sávot.">
          <Oszlopdiagram napi={d.napi} nev="Beérkezett leadek naponta" />
        </Blokk>
        <Blokk cim="Miért vesznek el" leiras={d.csak_erdeklodo.db > 0
          ? `A lezárt, elveszett leadek oka. A megerősített leadek ${aranySzoveg(d.csak_erdeklodo.arany)}-a csak érdeklődő volt, eladási szándék nélkül.`
          : 'A lezárt, elveszett leadek oka.'}>
          {d.elveszett_okok.length > 0
            ? <SorLista nev="Elveszett leadek oka" szin="arany" sorok={d.elveszett_okok.map((o) => ({ cimke: elvesztOkCimke(o.ok), db: o.db }))} />
            : <UresSor szoveg="Ebben az időszakban nem veszett el lead." />}
        </Blokk>
      </div>

      <Blokk cim="Később lesz aktuális" leiras="A most várakozó leadek, az időszaktól függetlenül: hónaponként, mikor kell újra keresni őket.">
        {d.kesobb_aktualis.db > 0 ? (
          <>
            <dl className="mt-4 grid grid-cols-2 gap-6 sm:max-w-md">
              <div><dt className="text-[13px] text-szoveg-halk">Várakozó lead</dt><dd className="szam-nagy mt-1 text-[26px]">{d.kesobb_aktualis.db}</dd></div>
              <div><dt className="text-[13px] text-szoveg-halk">Esedékes újrahívás</dt><dd className="szam-nagy mt-1 text-[26px]">{d.kesobb_aktualis.esedekes_db}</dd></div>
            </dl>
            {d.kesobb_aktualis.honapok.length > 0 && (
              <SorLista nev="Újrahívás hónaponként" szin="smaragd" sorok={d.kesobb_aktualis.honapok.map((h) => ({ cimke: honapCimke(`${h.honap}-01`), db: h.db }))} />
            )}
          </>
        ) : <UresSor szoveg="Most nincs „később aktuális” lead." />}
      </Blokk>

      <Blokk cim="Honnan jönnek a jó leadek" leiras="Kampányonként: mennyi lead jött, és mennyiből lett szemle vagy megbízás.">
        {d.forrasok.length > 0 ? (
          <Tabla nev="Leadek forrásonként" fej={[{ cim: 'Forrás és kampány' }, { cim: 'Lead', j: true }, { cim: 'Arány' }, { cim: 'Szemle', j: true }, { cim: 'Megbízás', j: true }]}>
            {d.forrasok.map((f) => (
              <tr key={`${f.forras}|${f.kampany}`}>
                <Cim nev={forrasCimke(f.forras)} alcim={kampanyCimke(f.kampany)} />
                <td className="j szam">{f.db}</td>
                <td><span aria-hidden className="block h-1.5 w-30 max-w-full"><i className="block h-full rounded-[3px] bg-kiemelt opacity-85" style={{ width: `${Math.max(2, (f.db / legnagyobbForras) * 100)}%` }} /></span></td>
                <td className="j szam">{szazalekOsszeg(f.szemle, f.db)}</td>
                <td className="j szam">{szazalekOsszeg(f.megbizas, f.db)}</td>
              </tr>
            ))}
          </Tabla>
        ) : <UresSor szoveg="Nincs forrásadat." />}
      </Blokk>

      <Blokk cim="Kollégák" leiras="Csak azok a leadek számítanak, amelyeket már kiosztottak valakinek.">
        {d.kollegak.length > 0 ? (
          <Tabla nev="Leadek kollégánként" fej={[{ cim: 'Kolléga' }, { cim: 'Lead', j: true }, { cim: 'Elérve', j: true }, { cim: 'Medián reakció', j: true }, { cim: 'Szemle', j: true }, { cim: 'Megbízás', j: true }]}>
            {d.kollegak.map((k) => (
              <tr key={k.user_id}>
                <Cim nev={k.nev} />
                <td className="j szam">{k.db}</td>
                <td className="j szam">{k.elerve}</td>
                <td className="j szam">{percSzoveg(k.reakcio_median_perc)}</td>
                <td className="j szam">{k.szemle}</td>
                <td className="j szam">{k.megbizas}</td>
              </tr>
            ))}
          </Tabla>
        ) : <UresSor szoveg="Ebben az időszakban még nem osztottak ki leadet." />}
      </Blokk>

      {d.irodak.length > 0 && (
        <Blokk cim="Irodák" leiras="Csak super adminnak: az irodák egymás mellett, ugyanarra az időszakra.">
          <Tabla nev="Irodák összehasonlítása" minSzel={720} fej={[{ cim: 'Iroda' }, { cim: 'Lead', j: true }, { cim: 'Medián reakció', j: true }, { cim: 'Határidőn belül', j: true }, { cim: 'Szemle', j: true }, { cim: 'Megbízás', j: true }, { cim: 'Csak érdeklődő', j: true }, { cim: 'SMS', j: true }]}>
            {d.irodak.map((i) => (
              <tr key={i.office_id}>
                <Cim nev={i.nev} />
                <td className="j szam">{i.db}</td>
                <td className="j szam">{percSzoveg(i.reakcio_median_perc)}</td>
                <td className="j szam">{aranySzoveg(i.hataridon_belul_arany)}</td>
                <td className="j szam">{aranySzoveg(i.szemle_arany)}</td>
                <td className="j szam">{aranySzoveg(i.megbizas_arany)}</td>
                <td className="j szam">{aranySzoveg(i.csak_erdeklodo_arany)}</td>
                <td className="j szam">{i.sms_db}</td>
              </tr>
            ))}
          </Tabla>
        </Blokk>
      )}

      <Blokk cim="Milyen ingatlanok érkeznek" leiras={d.ingatlanok.szakertoi_arany === null ? 'Típus, terület, árelvárás és a kért hívási időszak.' : `Típus, terület, árelvárás és a kért hívási időszak. A leadek ${aranySzoveg(d.ingatlanok.szakertoi_arany)}-a kapott szakértői becslést (szám nélkül).`}>
        <div className="mt-2 grid gap-x-10 gap-y-6 sm:grid-cols-2 xl:grid-cols-4">
          {ingatlanLista('Típus', d.ingatlanok.tipusok.map((t) => ({ cimke: tipusCimke(t.tipus), db: t.db })))}
          {ingatlanLista('Terület', d.ingatlanok.teruletek.map((t) => ({ cimke: t.nev, db: t.db })))}
          {ingatlanLista('Árelvárás', d.ingatlanok.arelvaras.map((t) => ({ cimke: arelvarasCimke(t.ertek), db: t.db })))}
          {ingatlanLista('Hívási időszak', d.ingatlanok.idosav.map((t) => ({ cimke: idosavCimke(t.ertek), db: t.db })))}
        </div>
      </Blokk>

      <Blokk cim="SMS-ek" leiras="Az időszakban kiküldött ellenőrző kódok. A kiküldött SMS-ek száma a számlázás alapja.">
        <dl className="mt-4 grid grid-cols-2 gap-6 sm:max-w-md">
          <div><dt className="text-[13px] text-szoveg-halk">Kódot kapott érdeklődő</dt><dd className="szam-nagy mt-1 text-[26px]">{d.sms.verify_db}</dd></div>
          <div><dt className="text-[13px] text-szoveg-halk">Kiküldött SMS</dt><dd className="szam-nagy mt-1 text-[26px]">{d.sms.ertesito_db}</dd></div>
        </dl>
      </Blokk>
    </div>
  );
}

